import React, { useState } from 'react';
import { Button, TwIconButton } from 'components/common';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import { FaCloudUploadAlt, FaTimes, FaTrash, FaFile } from 'react-icons/fa';
import { uploadAsset } from 'redux/drive/driveSlice';
import { getSize } from 'helpers/utils';
const MAX_FILE_SIZE = 500 * 1024 * 1024;

const DriveUploadModal = ({ show, onClose, assetId, onSuccess }) => {
  const dispatch = useDispatch();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const reset = () => { setFiles([]); setUploading(false); };
  const closeModal = () => {
    if (uploading) return;
    reset();
    onClose();
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: true,
    onDrop: (accepted) => {
      const filtered = accepted.filter((f) => {
        if (f.name.toLowerCase().endsWith('.exe')) return false;
        if (f.size > MAX_FILE_SIZE) {
          toast.warning(`${f.name} exceeds 500MB`);
          return false;
        }
        return true;
      });
      setFiles((prev) => [...prev, ...filtered]);
    },
  });

  const handleSubmit = async () => {
    if (!files.length) {
      toast.warning('Select at least one file');
      return;
    }
    setUploading(true);
    try {
      await dispatch(uploadAsset({ assetId, data: { documents: files } })).unwrap();
      toast.success(`Uploaded ${files.length} file(s)`);
      reset();
      onClose();
      onSuccess?.();
    } catch (err) {
      toast.error(err?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  if (!show) return null;

  const modal = (
    <div
      className="tw-fixed tw-inset-0 tw-flex tw-items-center tw-justify-center tw-p-4"
      style={{ zIndex: 999999, backgroundColor: 'rgba(15,23,42,0.5)' }}
      onClick={closeModal}
    >
      <div
        className="tw-bg-white tw-rounded tw-shadow-xl tw-w-full tw-max-w-xl tw-overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3 tw-border-b tw-border-slate-100">
          <h3 className="tw-text-base tw-font-bold tw-text-slate-900 tw-m-0">Upload Files</h3>
          <TwIconButton icon={FaTimes} size="md" variant="ghost" onClick={closeModal} disabled={uploading} title="Close" />
        </div>

        <div className="tw-p-5">
          <div
            {...getRootProps({
              className: [
                'tw-border-2 tw-border-dashed tw-rounded-md tw-p-6 tw-text-center tw-cursor-pointer tw-transition-all',
                isDragActive ? 'tw-border-slate-900 tw-bg-slate-50' : 'tw-border-slate-300 hover:tw-border-slate-400 hover:tw-bg-slate-50',
              ].join(' '),
            })}
          >
            <input {...getInputProps()} />
            <div className="tw-flex tw-flex-col tw-items-center">
              <div className="tw-w-12 tw-h-12 tw-bg-slate-100 tw-border tw-border-slate-200 tw-rounded-md tw-flex tw-items-center tw-justify-center tw-mb-3 tw-text-slate-700">
                <FaCloudUploadAlt size={24} />
              </div>
              <p className="tw-text-sm tw-font-bold tw-text-slate-900 tw-m-0">
                {isDragActive ? 'Drop files here' : 'Drop files here'}
              </p>
              <p className="tw-text-xs tw-font-medium tw-text-slate-500 tw-m-0 tw-mt-1">or click to browse</p>
              <div className="tw-mt-3 tw-flex tw-items-center tw-gap-2 tw-text-[11px] tw-font-bold tw-text-slate-400 tw-tracking-wider">
                <span>MAX 500MB</span>
                <span className="tw-w-1 tw-h-1 tw-bg-slate-300 tw-rounded-[999px]" />
                <span>NO .EXE</span>
              </div>
            </div>
          </div>

          {files.length > 0 && (
            <div className="tw-mt-4 tw-border tw-border-slate-200 tw-rounded-md tw-max-h-56 tw-overflow-y-auto">
              {files.map((f, idx) => (
                <div key={`${f.name}-${idx}`} className="tw-flex tw-items-center tw-gap-3 tw-px-3 tw-py-2 tw-border-b tw-border-slate-100 last:tw-border-b-0">
                  <FaFile className="tw-text-slate-400 tw-shrink-0" />
                  <div className="tw-flex-1 tw-min-w-0">
                    <p className="tw-text-xs tw-font-bold tw-text-slate-800 tw-truncate tw-m-0" title={f.name}>{f.name}</p>
                    <p className="tw-text-[11px] tw-font-mono tw-text-slate-500 tw-m-0">{getSize(f.size)}</p>
                  </div>
                  <TwIconButton
                    icon={FaTrash}
                    size="sm"
                    variant="danger"
                    onClick={() => setFiles((prev) => prev.filter((_, i) => i !== idx))}
                    disabled={uploading}
                    title="Remove"
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-px-5 tw-py-3 tw-border-t tw-border-slate-100 tw-bg-slate-50">
          <Button variant="secondary" size="md" onClick={closeModal} disabled={uploading}>Cancel</Button>
          <Button
            variant="primary"
            size="md"
            leftIcon={FaCloudUploadAlt}
            onClick={handleSubmit}
            disabled={!files.length}
            loading={uploading}
          >
            {uploading ? 'Uploading...' : `Upload${files.length ? ` (${files.length})` : ''}`}
          </Button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

DriveUploadModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  assetId: PropTypes.string,
  onSuccess: PropTypes.func,
};

export default DriveUploadModal;
